<template>
  <div>
    <div id="billing-dataview" class="page-table scrollable">
      <div class="flex align-center mt-5 mb-10">
        <el-select
          v-model="billDate"
          placeholder="Select Billing Month"
          size="large"
          filterable
        >
          <el-option
            v-for="(month, index) in months"
            :key="index"
            :label="month.label"
            :value="month.value"
          />
        </el-select>

        <el-button style="margin-left: 1rem" @click="doExport">
          Export
        </el-button>

        <div v-if="total > limit" style="margin-left: auto">
          <el-pagination
            small
            background
            layout="prev, pager, next"
            @current-change="pageSet"
            @next-click="pageNext"
            @prev-click="pagePrevious"
            :current-page="page"
            :page-size="limit"
            :pager-count="5"
            :total="total"
          />
        </div>
      </div>

      <div class="billing-table card-base card-shadow--medium">
        <vxe-grid
          round
          class="user-stats-table"
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 6, oSize: 0 }"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ExportToCsv } from "export-to-csv";
import { add, debounce } from "lodash";
import feathersClient from "@/feathers/feathers-client";

export default {
  name: "PartnerInventory",
  props: {
    months: { required: true }
  },
  components: {},
  data() {
    return {
      total: 0,
      tableData: [],
      tableLoading: false,
      billDate: null,
      page: 1,
      pages: 1,
      limit: 300,
      tableColumns: [
        {
          field: "accountBillingCode",
          title: "Billing Code",
          sortable: true,
          minWidth: 150,
          showOverflow: true
        },
        {
          field: "accountName",
          title: "Account Name",
          sortable: true,
          minWidth: 200,
          showOverflow: true
        },
        {
          field: "extensionNumber",
          title: "Extension Number",
          sortable: true,
          minWidth: 150,
          showOverflow: true
        },
        {
          field: "extensionName",
          title: "Extension Name",
          sortable: true,
          minWidth: 200,
          showOverflow: false
        },
        {
          field: "extensionCallerId",
          title: "Extension Caller Id",
          sortable: true,
          minWidth: 150,
          showOverflow: false
        },
        {
          field: "phoneMac",
          title: "Phone MAC",
          sortable: true,
          minWidth: 150,
          showOverflow: false
        },
        {
          field: "phoneName",
          title: "Phone Name",
          sortable: true,
          minWidth: 150,
          showOverflow: false
        },
        {
          field: "phoneModel",
          title: "Phone Model",
          sortable: true,
          minWidth: 150,
          showOverflow: false
        },
        {
          field: "e911Location",
          title: "e911 Location",
          sortable: true,
          minWidth: 300,
          showOverflow: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.addressFormatter(row.e911Location)}</span>];
            }
          }
        }
      ]
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    user() {
      return this.$store.state.auth.user;
    }
  },

  watch: {
    page: debounce(function() {
      this.findInventories();
    }, 500),

    months(val) {
      if (val && val.length) {
        this.billDate = val[0].value;
      }
    },

    billDate: debounce(function() {
      this.findInventories();
    }, 500)
  },

  methods: {
    doExport() {
      this.$confirm(`Export data for ${this.total} partner inventories?`, {
        type: "warning"
      })
        .then(async () => {
          this.tableLoading = true;

          let exportData = [];

          if (this.pages === 1) {
            exportData = [...this.tableData];
          }

          if (this.pages > 1) {
            const res = await feathersClient.service("billing").find({
              query: {
                context: "partnerInventory",
                tenantId: this.selectedTenant.id,
                partnerId: this.selectedTenant.partnerId,
                billDate: this.billDate,
                exportCSV: true
              }
            });

            exportData = res.data;
          }

          if (exportData.length) {
            const writeData = exportData.map(item => ({
              accountBillingCode: item.accountBillingCode,
              accountName: item.accountName,
              extensionNumber: item.extensionNumber,
              extensionName: item.extensionName,
              extensionCallerId: item.extensionCallerId,
              phoneMac: item.phoneMac,
              phoneName: item.phoneName,
              phoneModel: item.phoneModel,
              e911Location: this.addressFormatter(item.e911Location)
            }));

            const options = {
              filename: `partner_billing_inventory_${this.billDate}`,
              showLabels: true,
              headers: [
                "Billing Code",
                "Account Name",
                "Extension Number",
                "Extension Name",
                "Extension Caller Id",
                "Phone MAC",
                "Phone Name",
                "Phone Model",
                "e911 Location"
              ]
            };

            const csvExporter = new ExportToCsv(options);
            csvExporter.generateCsv(writeData);

            this.tableLoading = false;
          }
        })
        .catch(() => {});
    },

    findInventories() {
      this.billingInventories = [];
      this.tableLoading = true;
      feathersClient
        .service("billing")
        .find({
          query: {
            context: "partnerInventory",
            partnerId: this.selectedTenant.partnerId,
            tenantId: this.selectedTenant.id,
            billDate: this.billDate,
            $limit: this.limit,
            $skip: this.page > 1 ? this.limit * (this.page - 1) : 0
          }
        })
        .then(res => {
          const { total, limit, data } = res;
          this.pages = Math.ceil(total / limit);
          this.total = total;
          this.tableData = [...data];
          this.tableLoading = false;
        })
        .catch(() => {
          this.tableLoading = false;
        });
    },

    getValidString(val) {
      return val &&
        typeof val === "string" &&
        !val.toLowerCase().includes("undefined")
        ? val + " "
        : "";
    },

    addressFormatter(address) {
      try {
        address = address.replace(/&quot;/g, '"');
        let {
          streetNum,
          streetInfo,
          location,
          city,
          state,
          postalCode
        } = JSON.parse(address);

        return `${this.getValidString(streetNum)}${this.getValidString(
          streetInfo
        )}${this.getValidString(location)}${this.getValidString(
          city
        )}${this.getValidString(state)}${this.getValidString(postalCode)}`;
      } catch (e) {
        return address;
      }
    },

    pageNext() {
      if (this.page < this.pages) {
        this.page += 1;
      }
    },

    pagePrevious() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },

    pageSet(page) {
      this.page = page;
    }
  }
};
</script>

<style lang="scss" scoped>
.billing-table {
  height: 450px;
}
</style>
